import { useState, useMemo } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatPercent, formatPrice, formatDate, paginate, truncate } from '../utils/helpers';
import { CartableModal } from '../components/ui/CartableModal';

// ─── Formulaire par défaut ────────────────────────────────────────────────────
const DEFAULT_FORM = {
  name: '', email: '', phone: '',
  category: 'C', vehicle_brand: '', vehicle_model: '',
  vehicle_plate: '', vehicle_year: '', language: 'fr',
};

// ─── Badge disponibilité ──────────────────────────────────────────────────────
const AvailBadge = ({ livreur }) => {
  const isNew = !livreur.is_available && (livreur.total_deliveries_completed ?? 0) === 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: livreur.is_available ? 'var(--green)' : 'var(--text3)',
          boxShadow: livreur.is_available ? '0 0 6px var(--green)' : 'none',
        }} />
        <span style={{ fontSize: 12, color: livreur.is_available ? 'var(--green)' : 'var(--text3)', fontWeight: 600 }}>
          {livreur.is_available ? 'Disponible' : 'Indisponible'}
        </span>
      </div>
      {isNew && (
        <span style={{ fontSize: 10, color: 'var(--orange)', fontStyle: 'italic', paddingLeft: 14 }}>
          Compte non activé
        </span>
      )}
    </div>
  );
};

// ─── Champ info-row réutilisable ──────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div className="info-row">
    <span className="info-label">{label}</span>
    <span className="info-value">{value ?? '—'}</span>
  </div>
);

export const Livreurs = () => {
  const toast = useToast();

  const { data: raw, loading, error, refetch, setData } = useApi(() => adminApi.getLivreursList());

  // Cache stats par livreur_id
  const [statsCache,   setStatsCache]   = useState({});
  const [loadingStats, setLoadingStats] = useState(false);

  // Filtres & pagination
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState('all');
  const [availFilter, setAvailFilter] = useState('all');
  const [page,        setPage]        = useState(1);

  // Modales
  const [createModal, setCreateModal] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [cartableModal, setCartableModal] = useState(null);
  const [editModal,   setEditModal]   = useState(null);   // livreur à éditer
  const [quotaModal,  setQuotaModal]  = useState(null);
  const [statsModal,  setStatsModal]  = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  // Formulaires
  const [form,      setForm]      = useState(DEFAULT_FORM);
  const [editForm,  setEditForm]  = useState({});
  const [quota,     setQuota]     = useState({ quota_min_daily: '', quota_max_daily: '' });
  const [permanent, setPermanent] = useState(false);

  // États de chargement
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  const livreurs    = raw?.data || [];
  const unavailCount = livreurs.filter(l => !l.is_available).length;
  const newCount     = livreurs.filter(l => !l.is_available && (l.total_deliveries_completed ?? 0) === 0).length;

  const filtered = useMemo(() => livreurs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || l.name?.toLowerCase().includes(q)
      || l.phone?.toLowerCase().includes(q)
      || l.email?.toLowerCase().includes(q)
      || l.vehicle_plate?.toLowerCase().includes(q);
    const matchCat   = catFilter   === 'all' || l.category === catFilter;
    const matchAvail = availFilter === 'all'
      || (availFilter === 'available' ? l.is_available : !l.is_available);
    return matchSearch && matchCat && matchAvail;
  }), [livreurs, search, catFilter, availFilter]);

  const paged = paginate(filtered, page, 10);

  // ── Ouvrir stats ────────────────────────────────────────────────────────────
  const openStats = async (livreur) => {
    setStatsModal(livreur);
    if (statsCache[livreur.livreur_id]) return;
    setLoadingStats(true);
    try {
      const res = await adminApi.getLivreurStats(livreur.livreur_id);
      setStatsCache(p => ({ ...p, [livreur.livreur_id]: res.data }));
    } catch {
      toast.error('Impossible de charger les statistiques');
    } finally {
      setLoadingStats(false);
    }
  };

  // ── Ouvrir édition ──────────────────────────────────────────────────────────
  const openEdit = (livreur) => {
    setEditModal(livreur);
    setEditForm({
      name:          livreur.name          ?? '',
      email:         livreur.email         ?? '',
      phone:         livreur.phone         ?? '',
      category:      livreur.category      ?? 'C',
      vehicle_brand: livreur.vehicle_brand ?? '',
      vehicle_model: livreur.vehicle_model ?? '',
      vehicle_plate: livreur.vehicle_plate ?? '',
      vehicle_year:  livreur.vehicle_year  ?? '',
      is_active:     livreur.is_active     ?? true,
    });
  };

  // ── Créer ───────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      toast.error('Nom et téléphone sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createLivreur({
        ...form,
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
      });
      const tmp = res.data?.temporary_password;
      setCreateModal(false);
      setForm(DEFAULT_FORM);
      refetch();
      if (tmp) {
        setCreatedInfo({ name: form.name, email: form.email, temporary_password: tmp });
      } else {
        toast.success(`Livreur "${form.name}" créé ✓ — email avec mot de passe temporaire envoyé à ${form.email}`);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  // ── Sauvegarder l'édition ───────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!editForm.name?.trim() || !editForm.phone?.trim()) {
      toast.error('Nom et téléphone sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateLivreur(editModal.livreur_id, {
        name:          editForm.name.trim(),
        email:         editForm.email.trim()    || null,
        phone:         editForm.phone.trim(),
        category:      editForm.category,
        vehicle_brand: editForm.vehicle_brand   || null,
        vehicle_model: editForm.vehicle_model   || null,
        vehicle_plate: editForm.vehicle_plate   || null,
        vehicle_year:  editForm.vehicle_year    ? Number(editForm.vehicle_year) : null,
        is_active:     editForm.is_active,
      });
      // Mise à jour optimiste de la liste
      setData(prev => ({
        ...prev,
        data: prev.data.map(l =>
          l.livreur_id === editModal.livreur_id ? { ...l, ...editForm } : l
        ),
      }));
      toast.success(`Livreur "${editForm.name}" mis à jour ✓`);
      setEditModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // ── Mettre à jour les quotas ────────────────────────────────────────────────
  const handleUpdateQuota = async () => {
    if (!quota.quota_min_daily || !quota.quota_max_daily) {
      toast.error('Remplissez les deux quotas.');
      return;
    }
    if (Number(quota.quota_min_daily) > Number(quota.quota_max_daily)) {
      toast.error('Le quota minimum ne peut pas dépasser le maximum.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateLivreurQuotas(quotaModal.livreur_id, {
        quota_min_daily: Number(quota.quota_min_daily),
        quota_max_daily: Number(quota.quota_max_daily),
      });
      setData(prev => ({
        ...prev,
        data: prev.data.map(l =>
          l.livreur_id === quotaModal.livreur_id
            ? { ...l, quota_min_daily: Number(quota.quota_min_daily), quota_max_daily: Number(quota.quota_max_daily) }
            : l
        ),
      }));
      toast.success('Quotas mis à jour ✓');
      setQuotaModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // ── Supprimer ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteLivreur(deleteModal.livreur_id, permanent);
      if (permanent) {
        setData(prev => ({ ...prev, data: prev.data.filter(l => l.livreur_id !== deleteModal.livreur_id) }));
        toast.success(`Livreur "${deleteModal.name}" supprimé définitivement.`);
      } else {
        setData(prev => ({
          ...prev,
          data: prev.data.map(l =>
            l.livreur_id === deleteModal.livreur_id
              ? { ...l, is_active: false, is_available: false }
              : l
          ),
        }));
        toast.success(`Livreur "${deleteModal.name}" désactivé. Les données sont préservées.`);
      }
      setDeleteModal(null);
      setPermanent(false);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Loading text="Chargement des livreurs..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      {/* Alerte livreurs indisponibles */}
      {unavailCount > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
          <span>ℹ️</span>
          <div>
            <strong>{unavailCount} livreur(s) indisponible(s)</strong>
            {newCount > 0 && <> dont <strong>{newCount} nouveau(x)</strong> jamais activé(s)</>}.
            Les livreurs activent leur disponibilité depuis l'application mobile après le premier changement de mot de passe.
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="Nom, téléphone, email, plaque..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select className="filter-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="all">Catégorie: Toutes</option>
          <option value="A">A — Premium</option>
          <option value="B">B — Standard</option>
          <option value="C">C — Économique</option>
        </select>
        <select className="filter-select" value={availFilter} onChange={e => { setAvailFilter(e.target.value); setPage(1); }}>
          <option value="all">Disponibilité: Tous</option>
          <option value="available">Disponible</option>
          <option value="busy">Indisponible</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} livreurs</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <button className="btn btn-primary" onClick={() => { setForm(DEFAULT_FORM); setCreateModal(true); }}>
          + Nouveau livreur
        </button>
      </div>

      {/* Tableau */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Livreur</th>
                <th>Catégorie</th>
                <th>Véhicule</th>
                <th>Disponibilité</th>
                <th>Note</th>
                <th>Livraisons</th>
                <th>Taux</th>
                <th>Quotas</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map(l => (
                <tr key={l.livreur_id} style={{ opacity: l.is_active === false ? 0.6 : 1 }}>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Avatar name={l.name} />
                      <div>
                        <div className="td-main">{l.name}</div>
                        <div className="td-sub">{l.phone}</div>
                        {l.email && <div className="td-sub">{l.email}</div>}
                        {l.is_active === false && (
                          <Badge className="badge-red" style={{ fontSize: 10, marginTop: 2 }}>Inactif</Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge className={l.category === 'A' ? 'badge-green' : l.category === 'B' ? 'badge-blue' : 'badge-gray'}>
                      Cat. {l.category}
                    </Badge>
                  </td>
                  <td>
                    <div className="td-main">
                      {truncate(`${l.vehicle_brand || ''} ${l.vehicle_model || ''}`.trim() || '—', 20)}
                    </div>
                    <div className="td-sub">{l.vehicle_plate || '—'}</div>
                    {l.vehicle_year && <div className="td-sub">{l.vehicle_year}</div>}
                  </td>
                  <td><AvailBadge livreur={l} /></td>
                  <td>
                    ⭐ {l.average_rating ?? '—'}
                    <span style={{ color: 'var(--text3)', fontSize: 11 }}> ({l.total_reviews ?? 0})</span>
                  </td>
                  <td>
                    <div>{l.total_deliveries_completed ?? l.total_deliveries ?? '—'}</div>
                    <div className="td-sub">{l.quota_current_daily ?? '—'} auj.</div>
                  </td>
                  <td>
                    <span style={{
                      fontWeight: 600,
                      color: (l.total_rejections ?? 0) > 5 ? 'var(--red)' : 'var(--green)',
                    }}>
                      {formatPercent(
                        l.total_deliveries_completed ?? 0,
                        ((l.total_deliveries_completed ?? 0) + (l.total_rejections ?? 0)) || 1
                      )}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                    {l.quota_min_daily ?? '—'} – {l.quota_max_daily ?? '—'}/j
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openStats(l)}>Stats</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setCartableModal({ id: l.user_id ?? l.livreur_id, name: l.name })}>📒</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(l)}>✏️</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setQuotaModal(l);
                        setQuota({ quota_min_daily: l.quota_min_daily ?? '', quota_max_daily: l.quota_max_daily ?? '' });
                      }}>
                        Quotas
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteModal(l)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paged.items.length === 0 && (
            <EmptyState
              icon="🛵"
              title="Aucun livreur"
              text="Créez le premier livreur ou modifiez les filtres."
              action={<button className="btn btn-primary" onClick={() => { setForm(DEFAULT_FORM); setCreateModal(true); }}>+ Nouveau livreur</button>}
            />
          )}
        </div>
        {paged.total > 0 && <div style={{ padding: '0 16px 16px' }}><Pagination {...paged} onPage={setPage} /></div>}
      </div>

      {/* ── CRÉER ─────────────────────────────────────────────────────────────── */}
      <Modal
        open={createModal}
        onClose={() => setCreateModal(false)}
        title="Créer un livreur"
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Créer le livreur'}
          </button>
        </>}
      >
        <div className="inline-alert inline-alert-blue" style={{ marginBottom: 20 }}>
          <span>📧</span>
          <div style={{ fontSize: 12 }}>
            Un mot de passe temporaire sera généré et envoyé par email.
            Le livreur sera créé <strong>indisponible</strong> — il activera lui-même sa disponibilité depuis l'application mobile.
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Nom complet *</label>
            <input className="form-input" placeholder="Ahmed Tazi" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Catégorie *</label>
            <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
              <option value="A">A — Premium</option>
              <option value="B">B — Standard</option>
              <option value="C">C — Économique</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" type="email" placeholder="livreur@mail.ma" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Téléphone *</label>
            <input className="form-input" placeholder="+212612..." value={form.phone}
              onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Langue de l'email</label>
            <select className="form-select" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}>
              <option value="fr">Français</option>
              <option value="ar">Arabe</option>
              <option value="en">Anglais</option>
            </select>
          </div>
        </div>

        <div className="divider" />
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--text2)' }}>🛵 Véhicule</div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Marque</label>
            <input className="form-input" placeholder="Honda" value={form.vehicle_brand}
              onChange={e => setForm(p => ({ ...p, vehicle_brand: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Modèle</label>
            <input className="form-input" placeholder="PCX 125" value={form.vehicle_model}
              onChange={e => setForm(p => ({ ...p, vehicle_model: e.target.value }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Plaque</label>
            <input className="form-input" placeholder="A-45678-23" value={form.vehicle_plate}
              onChange={e => setForm(p => ({ ...p, vehicle_plate: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Année</label>
            <input className="form-input" type="number" min="2000" max="2030" placeholder="2022" value={form.vehicle_year}
              onChange={e => setForm(p => ({ ...p, vehicle_year: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ── MOT DE PASSE TEMPORAIRE ───────────────────────────────────────────── */}
      <Modal
        open={!!createdInfo}
        onClose={() => setCreatedInfo(null)}
        title="✅ Livreur créé avec succès"
        size="sm"
        footer={<button className="btn btn-primary btn-full" onClick={() => setCreatedInfo(null)}>Fermer</button>}
      >
        {createdInfo && (
          <div>
            <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
              <span>✅</span>
              <div>
                Compte de <strong>{createdInfo.name}</strong> créé.
                Email envoyé à <strong>{createdInfo.email}</strong>.
              </div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                🔑 Mot de passe temporaire
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: 3,
                color: 'var(--accent)', userSelect: 'all', cursor: 'text',
                textAlign: 'center', padding: '8px 0',
              }}>
                {createdInfo.temporary_password}
              </div>
            </div>
            <div className="inline-alert inline-alert-yellow">
              <span>⚠️</span>
              <div style={{ fontSize: 12 }}>
                Le livreur devra <strong>changer ce mot de passe</strong> à la première connexion.
                Il sera <strong>indisponible</strong> jusqu'à ce qu'il active sa disponibilité dans l'app.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── ÉDITER ────────────────────────────────────────────────────────────── */}
      <Modal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Modifier — ${editModal?.name}`}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal(null)} disabled={saving}>Annuler</button>
          <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '💾 Sauvegarder'}
          </button>
        </>}
      >
        {editModal && (
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: 'var(--text2)' }}>👤 Informations personnelles</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nom complet *</label>
                <input className="form-input" value={editForm.name}
                  onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select className="form-select" value={editForm.category}
                  onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}>
                  <option value="A">A — Premium</option>
                  <option value="B">B — Standard</option>
                  <option value="C">C — Économique</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={editForm.email}
                  onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Téléphone *</label>
                <input className="form-input" value={editForm.phone}
                  onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
            </div>

            <div className="divider" />
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: 'var(--text2)' }}>🛵 Véhicule</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Marque</label>
                <input className="form-input" placeholder="Honda" value={editForm.vehicle_brand}
                  onChange={e => setEditForm(p => ({ ...p, vehicle_brand: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Modèle</label>
                <input className="form-input" placeholder="PCX 125" value={editForm.vehicle_model}
                  onChange={e => setEditForm(p => ({ ...p, vehicle_model: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plaque</label>
                <input className="form-input" value={editForm.vehicle_plate}
                  onChange={e => setEditForm(p => ({ ...p, vehicle_plate: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Année</label>
                <input className="form-input" type="number" min="2000" max="2030" value={editForm.vehicle_year}
                  onChange={e => setEditForm(p => ({ ...p, vehicle_year: e.target.value }))} />
              </div>
            </div>

            <div className="divider" />
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14, color: 'var(--text2)' }}>⚙️ Statut du compte</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!editForm.is_active}
                onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: 'var(--green)' }}
              />
              <span>Compte actif</span>
              {!editForm.is_active && (
                <span style={{ fontSize: 11, color: 'var(--orange)', marginLeft: 4 }}>
                  — Le livreur ne pourra pas se connecter
                </span>
              )}
            </label>
          </div>
        )}
      </Modal>

      {/* ── QUOTAS ────────────────────────────────────────────────────────────── */}
      <Modal
        open={!!quotaModal}
        onClose={() => setQuotaModal(null)}
        title={`Quotas — ${quotaModal?.name}`}
        size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setQuotaModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleUpdateQuota} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Mettre à jour'}
          </button>
        </>}
      >
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Missions minimum et maximum par jour pour ce livreur.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Quota min / jour</label>
            <input className="form-input" type="number" min="0" value={quota.quota_min_daily}
              onChange={e => setQuota(p => ({ ...p, quota_min_daily: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Quota max / jour</label>
            <input className="form-input" type="number" min="1" value={quota.quota_max_daily}
              onChange={e => setQuota(p => ({ ...p, quota_max_daily: e.target.value }))} />
          </div>
        </div>
      </Modal>

      {/* ── STATS ─────────────────────────────────────────────────────────────── */}
      <Modal
        open={!!statsModal}
        onClose={() => setStatsModal(null)}
        title={`Statistiques — ${statsModal?.name}`}
        size="md"
        footer={
          <button className="btn btn-secondary btn-sm" onClick={() => { setStatsModal(null); openEdit(statsModal); }}>
            ✏️ Modifier ce livreur
          </button>
        }
      >
        {loadingStats ? <Loading text="Chargement des statistiques..." />
          : statsModal && (() => {
              const s = statsCache[statsModal.livreur_id];
              if (!s) return <div style={{ color: 'var(--text2)', fontSize: 13 }}>Statistiques non disponibles.</div>;
              return (
                <div>
                  {/* Badge dispo */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: s.is_available ? 'rgba(74,240,160,0.07)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${s.is_available ? 'rgba(74,240,160,0.2)' : 'var(--border)'}`,
                    borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                  }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: s.is_available ? 'var(--green)' : 'var(--text3)' }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: s.is_available ? 'var(--green)' : 'var(--text2)' }}>
                      {s.is_available ? 'Disponible' : 'Indisponible'}
                    </span>
                    {!s.is_available && (
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        — Le livreur active sa disponibilité depuis l'app mobile
                      </span>
                    )}
                  </div>

                  {/* KPIs livraisons */}
                  <div className="grid-2" style={{ gap: 12, marginBottom: 16 }}>
                    {[
                      { l: 'Collectes',   v: s.deliveries?.total_pickups },
                      { l: 'Livraisons',  v: s.deliveries?.total_deliveries },
                      { l: 'Refus',       v: s.deliveries?.total_rejections },
                      { l: 'Taux succès', v: formatPercent(s.deliveries?.total_deliveries ?? 0, s.deliveries?.total_pickups || 1) },
                    ].map((k, i) => (
                      <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.l}</div>
                        <div style={{ fontWeight: 700, fontFamily: 'Syne', fontSize: 20 }}>{k.v ?? '—'}</div>
                      </div>
                    ))}
                  </div>

                  {/* Infos profil */}
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Profil</div>
                  <InfoRow label="Catégorie"        value={`Catégorie ${s.category ?? statsModal.category}`} />
                  <InfoRow label="Email"             value={s.email ?? statsModal.email} />
                  <InfoRow label="Téléphone"         value={s.phone ?? statsModal.phone} />
                  <InfoRow label="Véhicule"          value={s.vehicle ?? (`${statsModal.vehicle_brand ?? ''} ${statsModal.vehicle_model ?? ''}`.trim() || null)} />
                  <InfoRow label="Plaque"            value={s.vehicle_plate ?? statsModal.vehicle_plate} />
                  <InfoRow label="Année véhicule"    value={statsModal.vehicle_year} />
                  <InfoRow label="Note moyenne"      value={`⭐ ${s.average_rating ?? '—'}/5 (${s.total_reviews ?? 0} avis)`} />
                  <InfoRow label="Points perf."      value={s.performance_points} />
                  <InfoRow label="Quota aujourd'hui" value={`${s.quotas?.current_daily ?? '—'} / ${s.quotas?.max_daily ?? '—'} (min: ${s.quotas?.min_daily ?? '—'})`} />
                  <InfoRow label="Membre depuis"     value={formatDate(s.created_at ?? statsModal.created_at)} />

                  {/* Stats courses v2.4 */}
                  {s.courses && (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 10px' }}>
                        Statistiques courses
                      </div>
                      <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
                        {[
                          { l: 'Total courses',  v: s.courses.total,          color: 'var(--accent)' },
                          { l: 'Pickups',        v: s.courses.pickup,         color: 'var(--orange)' },
                          { l: 'Deliveries',     v: s.courses.delivery,       color: 'var(--blue)'   },
                          { l: 'Distance totale',v: s.courses.total_distance != null ? `${s.courses.total_distance} km` : null, color: 'var(--green)' },
                        ].map((k, i) => (
                          <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.l}</div>
                            <div style={{ fontWeight: 700, fontFamily: 'Syne', fontSize: 18, color: k.color }}>{k.v ?? '—'}</div>
                          </div>
                        ))}
                      </div>
                      {[
                        { label: 'Durée moy. pickup',   val: s.courses.avg_duration_pickup,   max: 30, color: 'var(--orange)', best: s.courses.fastest_pickup },
                        { label: 'Durée moy. delivery', val: s.courses.avg_duration_delivery, max: 45, color: 'var(--blue)',   best: s.courses.fastest_delivery },
                      ].map((row, i) => {
                        const pct = row.val ? Math.min(100, Math.round((row.val / row.max) * 100)) : 0;
                        return (
                          <div key={i} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: row.color }}>{row.val ? `${row.val} min` : '—'}</span>
                                {row.best != null && <span style={{ fontSize: 11, color: 'var(--text3)' }}>⚡ {row.best} min</span>}
                              </div>
                            </div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${pct}%`, background: row.color }} />
                            </div>
                          </div>
                        );
                      })}
                      {s.courses.avg_distance_per_course != null && (
                        <InfoRow label="Dist. moy./course" value={`${s.courses.avg_distance_per_course} km`} />
                      )}
                    </>
                  )}

                  {/* Performance QR v2.4 */}
                  {s.qr_scans && (
                    <>
                      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '20px 0 10px' }}>
                        Performance QR codes
                      </div>
                      {[
                        { label: 'QR1 (Pickup client)',  val: s.qr_scans.qr1_scanned, total: s.courses?.pickup   || 0, color: 'var(--blue)'   },
                        { label: 'QR3 (Pickup laverie)', val: s.qr_scans.qr3_scanned, total: s.courses?.delivery || 0, color: 'var(--orange)' },
                        { label: 'QR4 (Delivery)',       val: s.qr_scans.qr4_scanned, total: s.courses?.delivery || 0, color: 'var(--green)'  },
                      ].map((qr, i) => {
                        const pct = qr.total > 0 ? Math.round((qr.val / qr.total) * 100) : 0;
                        return (
                          <div key={i} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{qr.label}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: qr.color }}>
                                {qr.val ?? '—'} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>({pct}%)</span>
                              </span>
                            </div>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${pct}%`, background: qr.color }} />
                            </div>
                          </div>
                        );
                      })}
                      {s.qr_scans.success_rate != null && (
                        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', textAlign: 'center', marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Taux de succès global</div>
                          <div style={{
                            fontWeight: 800, fontSize: 28, fontFamily: 'Syne',
                            color: s.qr_scans.success_rate >= 90 ? 'var(--green)' : s.qr_scans.success_rate >= 70 ? 'var(--orange)' : 'var(--red)',
                          }}>
                            {s.qr_scans.success_rate}%
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()
        }
      </Modal>

      {/* ── SUPPRESSION ───────────────────────────────────────────────────────── */}
      <Modal
        open={!!deleteModal}
        onClose={() => { setDeleteModal(null); setPermanent(false); }}
        title={`Supprimer — ${deleteModal?.name}`}
        size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => { setDeleteModal(null); setPermanent(false); }} disabled={deleting}>
            Annuler
          </button>
          <button
            className={`btn ${permanent ? 'btn-danger' : 'btn-secondary'}`}
            onClick={handleDelete}
            disabled={deleting}
            style={permanent ? {} : { borderColor: 'var(--orange)', color: 'var(--orange)', background: 'rgba(240,160,74,0.08)' }}
          >
            {deleting
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
              : permanent ? '🗑 Supprimer définitivement' : '⏸ Désactiver'}
          </button>
        </>}
      >
        {deleteModal && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Choisissez le type de suppression pour <strong>{deleteModal.name}</strong> :
            </p>
            {[
              {
                key: false, icon: '⏸', title: 'Désactivation (recommandé)', sub: 'Soft delete · Réversible',
                color: 'var(--accent)',
                items: ['is_active = false, is_available = false', 'Aucune mission ne lui sera assignée', 'Données et historique préservés', 'Réactivable à tout moment'],
              },
              {
                key: true, icon: '🗑', title: 'Suppression définitive', sub: 'Hard delete · IRRÉVERSIBLE',
                color: 'var(--red)',
                items: ['Suppression complète de la base de données', 'Livraisons, notes, assignations supprimées', 'Commandes historiques anonymisées', '⚠️ Action irréversible'],
              },
            ].map(opt => (
              <div
                key={String(opt.key)}
                onClick={() => setPermanent(opt.key)}
                style={{
                  border: `2px solid ${permanent === opt.key ? opt.color : 'var(--border)'}`,
                  borderRadius: 10, padding: '12px 14px', marginBottom: 10, cursor: 'pointer',
                  background: permanent === opt.key ? `${opt.color}10` : 'var(--bg3)', transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                  <span style={{ fontSize: 20 }}>{opt.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: permanent === opt.key ? opt.color : 'var(--text)' }}>{opt.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{opt.sub}</div>
                  </div>
                  {permanent === opt.key && <span style={{ marginLeft: 'auto', color: opt.color, fontWeight: 700 }}>✓</span>}
                </div>
                <ul style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                  {opt.items.map((item, i) => <li key={i} style={opt.key && i === 3 ? { color: 'var(--red)', fontWeight: 600 } : {}}>{item}</li>)}
                </ul>
              </div>
            ))}
            {permanent && (
              <div className="inline-alert inline-alert-red" style={{ marginTop: 12 }}>
                <span>⚠️</span>
                <span>Vous êtes sur le point de <strong>supprimer définitivement</strong> ce livreur. Cette action ne peut pas être annulée.</span>
              </div>
            )}
          </div>
        )}
      </Modal>
      {/* ── CARTABLE ── */}
      <CartableModal
        open={!!cartableModal}
        onClose={() => setCartableModal(null)}
        userId={cartableModal?.id}
        userName={cartableModal?.name}
      />
    </div>
  );
};